import type { AnyRecord, RecordApiKind, RecordListQuery } from '@fruuit/shared'
import { cn, controlClasses, Field, Spinner } from '@fruuit/ui'
import { Search, X } from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'
import { useRecord, useRecordList } from './api'
import { recordLabel } from './labels'
import { useDebouncedValue } from './useDebouncedValue'

interface PickerBase {
  kind: RecordApiKind
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  /** Extra list filters, e.g. `{ state: 'published' }` or `{ issuer_id }`. */
  filter?: RecordListQuery
  placeholder?: string
  disabled?: boolean
  className?: string
}

export interface RecordPickerProps extends PickerBase {
  value: string | null
  onChange: (id: string | null, record: AnyRecord | null) => void
}

export interface RecordMultiPickerProps extends PickerBase {
  value: string[]
  onChange: (ids: string[]) => void
  max?: number
}

/** Label for a selected id: fetched once and cached (FORM-003 "Choose a record"). */
export function RecordName({ kind, id }: { kind: RecordApiKind; id: string }) {
  const { data, isError } = useRecord(kind, id, { staleTime: 60_000 })
  if (data) return <>{recordLabel(data as AnyRecord)}</>
  return <span className="text-sage">{isError ? `Unavailable record ${id}` : 'Loading…'}</span>
}

function SearchResults({
  kind,
  term,
  filter,
  exclude,
  onPick,
  listId,
}: {
  kind: RecordApiKind
  term: string
  filter: RecordListQuery | undefined
  exclude: readonly string[]
  onPick: (record: AnyRecord) => void
  listId: string
}) {
  const q = useDebouncedValue(term)
  const { data, isFetching, isError } = useRecordList(kind, {
    ...filter,
    q: q || undefined,
    limit: 8,
  })
  const items = (data?.items ?? []).filter((item) => !exclude.includes(item.id))
  return (
    <div className="absolute inset-x-0 top-full z-20 mt-1 overflow-hidden rounded-md border border-line bg-surface shadow-pop">
      {isError ? (
        <p className="px-3 py-2.5 text-sm text-danger">Search is unavailable right now.</p>
      ) : items.length === 0 ? (
        <p className="flex items-center gap-2 px-3 py-2.5 text-sm text-sage">
          {isFetching ? <Spinner className="size-3.5" /> : null}
          {isFetching ? 'Searching…' : 'No records match this search.'}
        </p>
      ) : (
        <ul id={listId} aria-label="Matching records" className="max-h-64 overflow-y-auto py-1">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onPick(item as AnyRecord)}
                className="block w-full px-3 py-2 text-left text-sm text-ink hover:bg-wash focus-visible:bg-wash focus-visible:outline-none"
              >
                {recordLabel(item as AnyRecord)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function SearchBox({
  control,
  kind,
  filter,
  exclude,
  placeholder,
  disabled,
  onPick,
}: {
  control: { id: string; 'aria-describedby': string | undefined; 'aria-invalid': true | undefined }
  kind: RecordApiKind
  filter: RecordListQuery | undefined
  exclude: readonly string[]
  placeholder: string
  disabled: boolean | undefined
  onPick: (record: AnyRecord) => void
}) {
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const listId = useId()
  return (
    // Results stay open while focus moves between the input and the result buttons.
    <fieldset
      className="relative m-0 min-w-0 border-0 p-0"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation()
          setOpen(false)
        }
      }}
    >
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-sage"
      />
      <input
        type="search"
        aria-controls={open ? listId : undefined}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        value={term}
        onChange={(event) => {
          setTerm(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        className={cn(controlClasses, 'h-11 pl-9')}
        {...control}
      />
      {open && !disabled ? (
        <SearchResults
          kind={kind}
          term={term}
          filter={filter}
          exclude={exclude}
          listId={listId}
          onPick={(record) => {
            onPick(record)
            setTerm('')
            setOpen(false)
          }}
        />
      ) : null}
    </fieldset>
  )
}

/** Searchable single-record select over `GET /records/:kind?q=` (FORM-003). */
export function RecordPicker({
  kind,
  label,
  hint,
  error,
  required,
  filter,
  placeholder = 'Search by name',
  disabled,
  className,
  value,
  onChange,
}: RecordPickerProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {(control) =>
        value ? (
          <div
            className={cn(
              controlClasses,
              'flex h-11 items-center justify-between gap-2 bg-wash/60 hover:border-line-strong',
            )}
          >
            <span id={control.id} className="min-w-0 truncate">
              <RecordName kind={kind} id={value} />
            </span>
            {disabled ? null : (
              <button
                type="button"
                onClick={() => onChange(null, null)}
                aria-label="Clear selection"
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-sage hover:bg-line-soft hover:text-ink"
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            )}
          </div>
        ) : (
          <SearchBox
            control={control}
            kind={kind}
            filter={filter}
            exclude={[]}
            placeholder={placeholder}
            disabled={disabled}
            onPick={(record) => onChange(record.id, record)}
          />
        )
      }
    </Field>
  )
}

/** Accessible multi-record picker with removable chips (replaces Ctrl-click selects, FORM-003). */
export function RecordMultiPicker({
  kind,
  label,
  hint,
  error,
  required,
  filter,
  placeholder = 'Search to add',
  disabled,
  className,
  value,
  onChange,
  max = 100,
}: RecordMultiPickerProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {(control) => (
        <div className="flex flex-col gap-2">
          {value.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5" aria-label="Selected records">
              {value.map((id) => (
                <li
                  key={id}
                  className="inline-flex max-w-full items-center gap-1 rounded-sm border border-line bg-wash py-0.5 pr-1 pl-2 text-xs font-medium text-ink"
                >
                  <span className="min-w-0 truncate">
                    <RecordName kind={kind} id={id} />
                  </span>
                  {disabled ? null : (
                    <button
                      type="button"
                      onClick={() => onChange(value.filter((other) => other !== id))}
                      aria-label="Remove"
                      className="inline-flex size-5 items-center justify-center rounded-xs text-sage hover:bg-line-soft hover:text-ink"
                    >
                      <X aria-hidden="true" className="size-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          {value.length < max ? (
            <SearchBox
              control={control}
              kind={kind}
              filter={filter}
              exclude={value}
              placeholder={placeholder}
              disabled={disabled}
              onPick={(record) => onChange([...value, record.id])}
            />
          ) : null}
        </div>
      )}
    </Field>
  )
}
